import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import type {
	ImportColumnMappingModel,
	ImportRunBulkResolutionModel,
	ImportRunDefaultsModel,
	ImportRunStatus,
} from '../import/import-run.models';
import { BaseEntity } from './base.entity';
import { UserEntity } from './user.entity';

/**
 * One execution of a record import (PD-12353): an uploaded file, its column
 * mapping snapshot, the per-run defaults, and aggregate outcome counts.
 * Per-row detail lives in import_run_row. Runs park in `needs_review` when
 * duplicate-matched rows are held for user resolution.
 */
@Index('idx_import_run_status', ['status'])
@Index('idx_import_run_importer_key', ['importerKey'])
@Index('idx_import_run_user', ['userId'])
// auditable: false - framework bookkeeping; progress patches would flood _audit.
@Entity('import_run', {}, { auditable: false })
export class ImportRunEntity extends BaseEntity {
	/** Which registered importer ran (e.g. 'client'). */
	@Column('varchar', { name: 'importerKey', length: 100 })
	public importerKey!: string;

	/** The list the import was launched from, for provenance (nullable — future non-list entry points). */
	@Column('varchar', { name: 'sourceListKey', length: 100, nullable: true })
	public sourceListKey!: string | null;

	/** Uploaded source file (files module id). */
	@Column('int', { name: 'fk_file__id', unsigned: true, nullable: true })
	public fileId!: number | null;

	/** Original file name as uploaded, for display. */
	@Column('varchar', { name: 'fileName', length: 255 })
	public fileName!: string;

	@Column('enum', {
		name: 'status',
		// 'cancelled' and 'resolving' (PD-16896) are widened onto existing
		// tenants by the AddImportRunCancelAndResolveStatuses migration; sync
		// never alters an enum.
		enum: [
			'pending',
			'processing',
			'needs_review',
			'completed',
			'failed',
			'cancelled',
			'resolving',
		],
		default: 'pending',
	})
	public status!: ImportRunStatus;

	/** Snapshot of the column mapping used for this run. */
	@Column('json', { name: 'mapping' })
	public mapping!: ImportColumnMappingModel;

	/** Per-run defaults (companies, warehouses, default salesperson, Lead vs Customer). */
	@Column('json', { name: 'runDefaults', nullable: true })
	public runDefaults!: ImportRunDefaultsModel | null;

	/** The user who launched the run. */
	/**
	 * PD-16927 — whether this run may notify anyone about the records it
	 * creates or updates (its summary, and any notification from resolving
	 * its held rows later). Chosen in the import wizard, on by default.
	 * Read through Boolean(): MySQL hands tinyint back as 0 / 1.
	 */
	@Column('boolean', { name: 'sendNotifications', default: true })
	public sendNotifications!: boolean;

	@Column('int', { name: 'fk_user__id', unsigned: true, nullable: true })
	public userId!: number | null;

	@ManyToOne(() => UserEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_user__id', referencedColumnName: 'id' }])
	public user!: UserEntity | null;

	@Column('int', { name: 'totalRows', unsigned: true, default: 0 })
	public totalRows!: number;

	@Column('int', { name: 'processedCount', unsigned: true, default: 0 })
	public processedCount!: number;

	@Column('int', { name: 'createdCount', unsigned: true, default: 0 })
	public createdCount!: number;

	@Column('int', { name: 'updatedCount', unsigned: true, default: 0 })
	public updatedCount!: number;

	@Column('int', { name: 'skippedCount', unsigned: true, default: 0 })
	public skippedCount!: number;

	@Column('int', { name: 'heldCount', unsigned: true, default: 0 })
	public heldCount!: number;

	@Column('int', { name: 'errorCount', unsigned: true, default: 0 })
	public errorCount!: number;

	/**
	 * Geocoding calls this run triggered. Workd pays for geocoding (tenants do
	 * not), so the count is recorded per run for cost/abuse visibility.
	 */
	@Column('int', { name: 'geocodeCallCount', unsigned: true, default: 0 })
	public geocodeCallCount!: number;

	/** PD-16090 — how many of those calls the free Census geocoder answered. */
	@Column('int', { name: 'geocodeCensusCount', unsigned: true, default: 0 })
	public geocodeCensusCount!: number;

	/** PD-16090 — how many fell back to paid Google geocoding. */
	@Column('int', { name: 'geocodeGoogleCount', unsigned: true, default: 0 })
	public geocodeGoogleCount!: number;

	/**
	 * PD-16107 — how many were served from the shared Mission Control geocode
	 * cache with no provider call at all. Counted separately so the Census and
	 * Google numbers above stay honest.
	 */
	@Column('int', { name: 'geocodeCacheCount', unsigned: true, default: 0 })
	public geocodeCacheCount!: number;

	@Column('datetime', { name: 'startedAt', nullable: true })
	public startedAt!: Date | null;

	@Column('datetime', { name: 'completedAt', nullable: true })
	public completedAt!: Date | null;

	@Column('text', { name: 'errorMessage', nullable: true })
	public errorMessage!: string | null;

	/**
	 * PD-16896: the bulk action being applied to the held rows while the run
	 * is `resolving`. Null otherwise.
	 */
	@Column('json', { name: 'bulkResolution', nullable: true })
	public bulkResolution!: ImportRunBulkResolutionModel | null;
}
